/**
 * A short explanation folded behind a button, for prose that is worth having
 * and not worth reading twice.
 *
 * WHY THIS EXISTS, AND THE LINE IT IS NOT ALLOWED TO CROSS
 *
 * Several screens carry a sentence that is genuinely useful the first time and
 * furniture every time after — how to read terms off an invoice, what to do
 * when a browser downloads a PDF instead of showing it. Left in the layout they
 * push the controls down the page and train the operator to skim, which is the
 * same failure as a warning that is always on screen: by the day it matters,
 * nobody sees it.
 *
 * THE BUTTON IS ALWAYS RENDERED. Only the prose collapses. That distinction is
 * the whole design, and it is what keeps this from being a way to hide things:
 * a reader who wonders "why is this frame blank" has a visible control sitting
 * exactly where the answer is, rather than having to know the answer exists.
 * CLAUDE.md's rule about the PDF notice — "the overlay states it instead, in a
 * line under the frame that is always rendered" — is met by the affordance
 * being permanent, not the paragraph.
 *
 * NOT A TOOLTIP. It opens on click and stays open until dismissed, because the
 * text it holds is two or three sentences that somebody needs while doing
 * something else with the mouse. A hover tooltip disappears the moment they
 * move toward the control it describes.
 *
 * `anchored` IS FOR A NOTE INSIDE SOMETHING THAT CANNOT GROW.
 *
 * The body is normally inline, flowing beside the button, which is right in a
 * section header. In a TABLE HEADER it was a disaster, reported 2026-09-18:
 * the span widened the `th`, so opening it re-laid-out every column in the
 * table; and it inherited the header's own styling, so two paragraphs of prose
 * rendered in uppercase monospace with letter-spacing, right-aligned, on top of
 * the other headings.
 *
 * Anchored, the body is taken out of the flow and painted as a card. The
 * container cannot change size, so nothing moves, and `.info-body` resets the
 * inherited text styling rather than trusting wherever it was dropped.
 */

import { useId, useState } from 'react';

export function InfoNote({
  label,
  anchored = false,
  children,
}: {
  /**
   * What the hidden text is about, for the button's accessible name. "More
   * information" tells a screen reader nothing about whether to open it.
   */
  label: string;
  /**
   * Float the body over the page instead of letting it flow inline.
   *
   * For a note inside a table header, a cell, or anything else whose width is
   * load-bearing. Inline is still the default, because that is right in a
   * section header and keeps the text in the reading order.
   */
  anchored?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <span className={`info-note${anchored ? ' is-anchored' : ''}`}>
      <button
        type="button"
        className="info-toggle"
        aria-expanded={open}
        aria-controls={id}
        aria-label={label}
        title={label}
        onClick={() => setOpen((current) => !current)}
      >
        i
      </button>
      {open && (
        <span className="info-body" id={id} role="note">
          {children}
        </span>
      )}
    </span>
  );
}
